// UPDATE v0.1 (2025-10-19): Добавлены поля клиента и каскадный выбор типа дела
// File: src/pages/NewCasePage.js

// --- 1. ИМПОРТЫ ---
// Импортируем хуки React для управления состоянием.
import React, { useState, useEffect } from 'react';
// Импортируем хуки для навигации и получения параметров
import { useNavigate, useLocation } from 'react-router-dom';
// Импортируем библиотеку для отображения уведомлений.
import toast from 'react-hot-toast';
// Импортируем наш централизованный сервис для работы с API.
import apiService from '../services/apiService';
// Импортируем компонент для отображения индикатора загрузки.
import LoadingSpinner from '../components/LoadingSpinner';
import { useUiLanguage } from '../context/UiLanguageContext';
// Импортируем стили, специфичные для этой страницы.
import './NewCasePage.css';

// Fallback список 11 областей практики ELN
const FALLBACK_PRACTICE_AREAS = [
  { key: 'estate-planning', label_en: 'Estate Planning', label_ru: 'Наследственное планирование', subtypes: [] },
  { key: 'probate', label_en: 'Probate', label_ru: 'Наследственное производство', subtypes: [] },
  { key: 'wills-and-trusts', label_en: 'Wills & Trusts', label_ru: 'Завещания и трасты', subtypes: [] },
  { key: 'asset-protection', label_en: 'Asset Protection', label_ru: 'Защита активов', subtypes: [] },
  { key: 'elder-law', label_en: 'Elder Law', label_ru: 'Право для пожилых', subtypes: [] },
  { key: 'guardianship', label_en: 'Guardianship', label_ru: 'Опека', subtypes: [] },
  { key: 'power-of-attorney', label_en: 'Power of Attorney', label_ru: 'Доверенности', subtypes: [] },
  { key: 'family-law', label_en: 'Family Law', label_ru: 'Семейное право', subtypes: [] },
  { key: 'real-estate', label_en: 'Real Estate', label_ru: 'Недвижимость', subtypes: [] },
  { key: 'matrimonial', label_en: 'Matrimonial Law', label_ru: 'Брачно-семейное право', subtypes: [] },
  { key: 'personal-injury', label_en: 'Personal Injury', label_ru: 'Вред здоровью', subtypes: [] },
];

// --- 2. КОМПОНЕНТ СТРАНИЦЫ ---
const NewCasePage = () => {
    const { t, lang } = useUiLanguage();
    const location = useLocation();
    const init = location.state || {};

    // --- 3. СОСТОЯНИЯ КОМПОНЕНТА (STATE) ---
    const [practiceAreas, setPracticeAreas] = useState(FALLBACK_PRACTICE_AREAS);

    // Информация о деле
    const [title, setTitle] = useState(init.title || ''); // Состояние для заголовка дела
    const [description, setDescription] = useState(init.description || ''); // Состояние для описания
    const [caseType, setCaseType] = useState(init.caseType || 'Estate Planning'); // Тип дела по умолчанию
    const [caseSubtype, setCaseSubtype] = useState(init.caseSubtype || ''); // Состояние для подтипа дела

    useEffect(() => {
        let isMounted = true;
        if (typeof apiService?.getPracticeAreas === 'function') {
            Promise.resolve(apiService.getPracticeAreas())
                .then((data) => {
                    const list = Array.isArray(data) ? data : (data?.areas || []);
                    if (isMounted && list.length > 0) {
                        setPracticeAreas(list);
                        if (!init.caseType) {
                            setCaseType(list[0].label_en);
                        }
                    }
                })
                .catch((err) => {
                    console.error('Failed to load practice areas from API:', err);
                });
        }
        return () => {
            isMounted = false;
        };
    }, [init.caseType]);

    // --- ДОБАВЛЕНО: Состояния для информации о клиенте ---
    const [clientEmail, setClientEmail] = useState(init.clientEmail || ''); // Email клиента
    const [clientFirstName, setClientFirstName] = useState(init.clientFirstName || ''); // Имя клиента
    const [clientLastName, setClientLastName] = useState(init.clientLastName || ''); // Фамилия клиента
    const [clientPhoneNumber, setClientPhoneNumber] = useState(init.clientPhoneNumber || ''); // Телефон клиента
    // --- КОНЕЦ ДОБАВЛЕНИЯ ---

    const [isLoading, setIsLoading] = useState(false); // Флаг загрузки
    const navigate = useNavigate(); // Хук для навигации

    // --- Функция для обновления подтипа при смене основного типа ---
    const handleCaseTypeChange = (e) => {
        const newType = e.target.value;
        setCaseType(newType);
        // Сбрасываем подтип, так как он зависит от основного типа
        setCaseSubtype('');
    };

    // --- 4. ОБРАБОТЧИК ОТПРАВКИ ФОРМЫ (ОБНОВЛЕН) ---
    const handleCreateCase = async (e) => {
        // Предотвращаем стандартное поведение формы (перезагрузку страницы).
        e.preventDefault();

        // --- Валидация: email ИЛИ телефон клиента обязателен ---
        const email = clientEmail.trim();
        const phone = clientPhoneNumber.trim();

        if (!email && !phone) {
            toast.error(t('practice.new_case.validation_email_or_phone', 'Client Email or Phone Number is required.'));
            return;
        }
        if (!title.trim()) {
            toast.error(t('practice.new_case.validation_title', 'Case Title is required.'));
            return;
        }

        setIsLoading(true);
        const toastId = toast.loading(t('practice.new_case.creating', 'Creating new case and client (if needed)...'));

        try {
            const caseData = {
                client_email: email || null,
                client_first_name: clientFirstName.trim() || t('practice.new_case.default_client_first_name', 'Client'),
                client_last_name: clientLastName.trim() || '',
                client_phone_number: phone || null,
                title: title.trim(),
                description: description.trim(),
                case_type: caseType,
                case_subtype: caseSubtype || null,
            };

            // Вызываем метод API (он уже готов принимать любой объект)
            const response = await apiService.createCase(caseData);

            toast.success(t('practice.new_case.success', { caseNumber: response?.case_number || '' }), { id: toastId }); // Уведомление об успехе

            // "Умное" перенаправление: если API вернул ID, переходим на страницу дела.
            if (response && response.id) {
                navigate(`/cases/${response.id}`);
            } else {
                navigate('/cases'); // Иначе переходим на список дел
            }

        } catch (error) {
            // --- ОБРАБОТКА ОШИБОК ---
            console.error("Failed to create case:", error); // Лог ошибки в консоль
            const errorMessage = error.message || 'An unexpected error occurred.'; // Получаем сообщение об ошибке
            // Отображаем ошибку валидации от Pydantic, если она есть
            toast.error(t('practice.new_case.failed', { error: errorMessage }), { id: toastId }); // Уведомление об ошибке
        } finally {
            // В любом случае выключаем индикатор загрузки
            setIsLoading(false);
        }
    };

    // --- 5. УСЛОВНЫЙ РЕНДЕРИНГ ---
    // Показываем спиннер вместо формы во время отправки
    if (isLoading) {
        return <LoadingSpinner message={t('practice.new_case.creating', 'Creating new case and client (if needed)...')} />;
    }

    // Получаем текущую область практики и её подтипы
    const currentArea = practiceAreas.find((a) => a.label_en === caseType || a.key === caseType) || practiceAreas[0];
    const currentSubtypes = currentArea?.subtypes || [];

    // --- 6. JSX-РАЗМЕТКА (ОБНОВЛЕНА) ---
    return (
        <div className="container">
            <div className="card new-case-card">
                <h1 className="card-header">{t('practice.new_case.title', 'Create a New Case')}</h1>
                <p className="new-case-subheader">
                    {t('practice.new_case.subtitle', "Enter client and case details below. If the client's email is new, a profile will be created automatically.")}
                </p>

                <form onSubmit={handleCreateCase}>

                    {/* --- ДОБАВЛЕНО: Секция информации о клиенте --- */}
                    <h2 className="section-header">{t('practice.new_case.section_client', 'Client Information')}</h2>
                    <div className="form-group">
                        <label htmlFor="client-email">{t('practice.new_case.email', 'Client Email')} <span className="required">*</span> <span style={{ fontSize: '0.8em', fontWeight: 'normal', color: 'var(--ink-muted-ink, #888)' }}>{t('practice.new_case.email_or_phone_required', '(Email or Phone required)')}</span></label>
                        <input
                            id="client-email"
                            type="email"
                            className="form-input"
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                            placeholder={t('practice.new_case.email_ph', 'e.g., client@example.com')}
                        />
                    </div>
                    <div className="form-row"> {/* Используем form-row для полей в одной строке */}
                       <div className="form-group">
                           <label htmlFor="client-first-name">{t('practice.new_case.first_name', 'First Name')}</label>
                           <input
                                id="client-first-name"
                                type="text"
                                className="form-input"
                                value={clientFirstName}
                                onChange={(e) => setClientFirstName(e.target.value)}
                                placeholder={t('practice.new_case.first_name_ph', "Client's first name")}
                           />
                       </div>
                       <div className="form-group">
                           <label htmlFor="client-last-name">{t('practice.new_case.last_name', 'Last Name')}</label>
                           <input
                                id="client-last-name"
                                type="text"
                                className="form-input"
                                value={clientLastName}
                                onChange={(e) => setClientLastName(e.target.value)}
                                placeholder={t('practice.new_case.last_name_ph', "Client's last name")}
                           />
                       </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="client-phone">{t('practice.new_case.phone', 'Phone Number')}</label>
                        <input
                            id="client-phone"
                            type="tel" // Используем tel для семантики и мобильной клавиатуры
                            className="form-input"
                            value={clientPhoneNumber}
                            onChange={(e) => setClientPhoneNumber(e.target.value)}
                            placeholder={t('practice.new_case.phone_ph', 'e.g., +12125551234')}
                        />
                    </div>
                    {/* --- КОНЕЦ СЕКЦИИ КЛИЕНТА --- */}


                    {/* --- Секция информации о деле --- */}
                    <h2 className="section-header">{t('practice.new_case.section_case', 'Case Details')}</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="case-type">{t('practice.new_case.case_type', 'Case Type')} <span className="required">*</span></label>
                            <select
                                id="case-type"
                                className="form-input"
                                value={caseType}
                                onChange={handleCaseTypeChange} // Используем новый обработчик
                                required
                            >
                                {practiceAreas.map((area) => (
                                    <option key={area.key} value={area.label_en}>
                                        {lang === 'ru' ? (area.label_ru || area.label_en) : area.label_en}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* --- Каскадный выбор подтипа --- */}
                        {currentSubtypes.length > 0 && (
                            <div className="form-group">
                                <label htmlFor="case-subtype">{t('practice.new_case.case_subtype', 'Case Subtype')}</label>
                                <select
                                    id="case-subtype"
                                    className="form-input"
                                    value={caseSubtype}
                                    onChange={(e) => setCaseSubtype(e.target.value)}
                                >
                                    <option value="">{t('practice.new_case.select_subtype', '-- Select Subtype --')}</option>
                                    {currentSubtypes.map((st) => (
                                        <option key={st.key} value={st.label_en}>
                                            {lang === 'ru' ? (st.label_ru || st.label_en) : st.label_en}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {/* --- КОНЕЦ КАСКАДНОГО ВЫБОРА --- */}
                    </div>

                    <div className="form-group">
                        <label htmlFor="case-title">{t('practice.new_case.case_title', 'Case Title')} <span className="required">*</span></label>
                        <input
                            id="case-title"
                            type="text"
                            className="form-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('practice.new_case.case_title_ph', "e.g., 'Estate Planning & Will for John Doe'")}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="case-description">{t('practice.new_case.description', 'Description')}</label>
                        <textarea
                            id="case-description"
                            rows="5"
                            className="form-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('practice.new_case.description_ph', 'Provide a brief summary of the case...')}
                        />
                    </div>
                    {/* --- КОНЕЦ СЕКЦИИ ДЕЛА --- */}

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)} // Кнопка "Cancel" возвращает назад
                            disabled={isLoading}
                        >
                            {t('practice.new_case.btn_cancel', 'Cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                        >
                            {isLoading ? t('practice.new_case.creating', 'Creating...') : t('practice.new_case.btn_create', 'Create Case')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- 7. ЭКСПОРТ ---
export default NewCasePage;